<template>
  <div>
    <component :is="wrapper">
      <template v-slot:postal>
        <textfield
          id="postal-code"
          :label="translate('lblPostalCode')"
          :pattern="pattern"
          :mandatory="mandatory"
          v-model="postalCode"
          autocomplete="off"
          @input="fetchPlace(false)"
          @change="fetchPlace(true)"
        ></textfield>
      </template>
      <template v-slot:place>
        <textfield
          id="place-name"
          :label="translate('lblPlaceName')"
          v-model="placeName"
          :mandatory="mandatory"
          autocomplete="off"
          @input="fetchPostalCode(false)"
          @change="fetchPostalCode(true)"
        >
          <span @click="getGeoLocation" class="location-icon" :title="translate('lblLocationDetect')"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="20" viewBox="0 0 12 20"><path fill="#333" d="M5.9925,0 C2.6785248,0 0,2.7254367 0,6.09755577 C0,6.87196974 0.2222256,7.85557866 0.48,8.5365781 L6,20 L11.52,8.5365781 C11.7792216,7.85531037 12,6.87231121 12,6.09755577 C12,2.7254367 9.3065184,0 5.9925,0 Z M5.9925,2.92682677 C7.7157888,2.92682677 9.12,4.34406946 9.12,6.09755577 C9.12,7.85106647 7.7157888,9.2682848 5.9925,9.2682848 C4.2692352,9.2682848 2.88,7.85106647 2.88,6.09755577 C2.88,4.34406946 4.2692352,2.92682677 5.9925,2.92682677 Z"/></svg></span>
        </textfield>
        <ul :class="['locations', locationsList && locationsList.length ? 'open' : '']">
          <li
            v-for="location in locationsList" :key="`${location.postalCode} ${location.placeName}`"
            v-text="`${location.postalCode} ${location.placeName}`"
            @click="applyLocation(location)"
          ></li>
        </ul>
      </template>
    </component>

    <notification-modal
      v-if="error"
      type="error"
      :title="translate('lblLocationErrorTitle')"
      :subtitle="translate('lblLocationErrorSubTitle')"
      :content="translate('lblLocationErrorText')"
      @close="error = false"
    ></notification-modal>

    <loading-indicator v-if="loading"></loading-indicator>
  </div>
</template>

<script>
import NotificationModal from './NotificationModal.vue';
import Textfield from './textfield.vue';
import LoadingMixin from '../mixins/LoadingMixin';

export default {
  name: 'PostalCodePlace',
  data: () => ({
    error: false,
    locationsList: [],
    postalCode: '',
    placeName: '',
    pattern: null,
    country: null,
    autocompletionEnabled: true,
  }),
  props: {
    postalCodePattern: String,
    postalCodeValue: String,
    cityValue: String,
    countryCode: {
      type: String,
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    wrapper: {
      type: [String, Object],
      default: 'div',
    },
    mandatory: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    debounce(callback) {
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(callback, 250);
    },
    getGeoLocation() {
      this.startRequest();

      navigator.geolocation.getCurrentPosition(async (position) => {
        const query = `query ($countryCode: String!, $lat: Float!, $lng: Float!) {
          position(latitude: $lat, longitude: $lng) {
            location(countryCode: $countryCode) {
              postalCode
              placeName
            }
          }
        }`;

        const result = await fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query,
            variables: {
              countryCode: this.country,
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          }),
        })
          .then((response) => response.json())
          .finally(() => { this.finishRequest(); });

        const { location } = result.data.position;

        if (location) {
          this.applyLocation(location);
        } else {
          this.error = true;
        }
      }, () => {
        this.finishRequest();
        this.error = true;
      }, {
        enableHighAccuracy: true,
        timeout: 5000,
      });
    },
    triggerChange(property) {
      switch (property) {
        case 'postalCode':
          this.triggerEvent('postalcodeChanged', { postalcode: this.postalCode });
          break;
        case 'placeName':
          this.triggerEvent('placeChanged', { place: this.placeName });
          break;
        default:
          console.warn('Invalid argument');
      }
      this.$emit('change', { postalCode: this.postalCode, placeName: this.placeName });
    },
    triggerEvent(eventName, detail) {
      document.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        cancelable: true,
        detail,
      }));
    },
    fetchPlace(changed = false) {
      if (!this.autocompletionEnabled) return;
      if (changed) this.triggerChange('postalCode');

      this.debounce(() => {
        const query = `query ($countryCode: String!, $search: String!) {
          locations(postalCode: $search, postalCodeMatching: STARTS_WITH, countryCode: $countryCode, maxResults: 1000) {
            postalCode
            placeName
          }
        }`;

        this.fetchLocation({
          search: this.postalCode,
          query,
          searchedProperty: 'placeName',
          fixProperty: changed ? 'postalCode' : null,
        });
      });
    },
    fetchPostalCode(changed = false) {
      if (!this.autocompletionEnabled) return;
      if (changed) this.triggerChange('placeName');

      this.debounce(() => {
        const query = `query ($countryCode: String!, $search: String!) {
        locations(placeName: $search, placeNameMatching: STARTS_WITH, countryCode: $countryCode, maxResults: 1000, ordering: PLACENAME_THEN_POSTALCODE) {
          postalCode
          placeName
        }
      }`;

        this.fetchLocation({
          search: this.placeName,
          query,
          searchedProperty: 'postalCode',
          fixProperty: changed ? 'placeName' : null,
        });
      });
    },
    async fetchLocation({
      search,
      query,
      searchedProperty,
      fixProperty,
    }) {
      this.locationsList = [];

      if (!search || search.length < 3) {
        return;
      }

      const result = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          variables: {
            countryCode: this.country,
            search,
          },
        }),
      }).then((response) => response.json());

      let { locations } = result.data;

      if (locations && locations.length) {
        const filteredLocations = locations
          .filter((location) => location[searchedProperty].startsWith(this[searchedProperty]));
        if (filteredLocations.length) {
          locations = filteredLocations;
        }
        const exactMatch = locations
          .filter((location) => location.placeName === this.placeName
            && location.postalCode === this.postalCode);
        this.locationsList = [];

        if (exactMatch.length !== 1) {
          if (locations.length > 1) {
            this.locationsList = locations;
          } else { // length === 1
            // eslint-disable-next-line no-lonely-if
            if (this[searchedProperty] !== locations[0][searchedProperty]) {
              // eslint-disable-next-line no-param-reassign
              this[searchedProperty] = locations[0][searchedProperty];
              this.triggerChange(searchedProperty);
            }

            if (fixProperty && this[fixProperty] !== locations[0][fixProperty]) {
              this[fixProperty] = locations[0][fixProperty];
              this.triggerChange(fixProperty);
            }
          }
        }
      }
    },
    applyLocation(location) {
      if (this.postalCode !== location.postalCode) {
        this.postalCode = location.postalCode;
        this.triggerChange('postalCode');
      }
      if (this.placeName !== location.placeName) {
        this.placeName = location.placeName;
        this.triggerChange('placeName');
      }
      this.locationsList = [];
    },
    initializeListeners() {
      document.addEventListener('countryChanged', (e) => {
        this.pattern = e.detail.country?.postalPattern || null;
        this.autocompletionEnabled = e.detail.country?.autoComplete || false;
        this.country = e.detail.country?.iso || this.countryCode;
      });

      document.addEventListener('postalcodeChanged', (e) => {
        this.postalCode = e.detail.postalcode || this.postalCodeValue;
      });

      document.addEventListener('placeChanged', (e) => {
        this.placeName = e.detail.place || this.cityValue;
      });
    },
  },
  mounted() {
    this.initializeListeners();
  },
  created() {
    this.postalCode = this.postalCodeValue;
    this.placeName = this.cityValue;
    this.pattern = this.postalCodePattern;
    this.country = this.countryCode;
    this.autocompletionEnabled = this.autocomplete && this.apiUrl && this.country;
  },
  mixins: [LoadingMixin],
  components: {
    NotificationModal,
    Textfield,
  },
};
</script>

<style scoped lang="scss">
.locations {
  z-index: 999;
  background-color: $color-white;
  max-height: 0;
  overflow-y: auto;
  list-style-type: none;
  padding: 0;
  margin: 0;
  transition: max-height .4s ease-in-out;

  &.open {
    max-height: 10rem;
  }

  li {
    position: static;
    padding: 0.375rem 0.375rem 0.375rem 0.6875rem;
    transition: margin-bottom .4s ease-out;
    cursor: pointer;
  }
}
</style>
